import React from 'react';
import PropTypes from 'prop-types';

import { useTheme } from 'contexts/ThemeContext';

import { Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import {
  AutocompleteTextField,
  Button,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';

const ErrorState = {
  funnelIdError: false
};

export const ModalForm = props => {
  const { themeColors } = useTheme();
  const { config, onValidate, onClose } = props;
  const { funnels = [], selected } = config;

  const initialState = {
    funnelId: selected?.funnelId,
    toleranceLimit: selected?.toleranceLimit || 10,
    toleranceLimits: selected?.toleranceLimits || [],
    emails: selected?.emails || [],
    sheetUrl: selected?.sheetUrl || '',
    type: selected?.type || 'budget',
    frequencyWeekly: selected?.alertingFrequence === 'weekly' || false,
    sendEmails: selected?.alerting ?? false
  };

  const [
    {
      funnelId,
      toleranceLimits,
      emails,
      sheetUrl,
      type,
      frequencyWeekly,
      sendEmails
    },
    setState
  ] = React.useState(initialState);
  const [{ funnelIdError }, setError] = React.useState(ErrorState);

  const resetStates = () => {
    setError(ErrorState);
  };

  const toggleError = (itemKey, state) => {
    setError(prevState => {
      const errors = { ...prevState };
      errors[`${itemKey}Error`] = state;
      return errors;
    });
  };

  const handleChange = itemKey => value => {
    const text = typeof value === 'string' ? value.trim() : value;
    toggleError(itemKey, !text);
    setState(prevState => ({
      ...prevState,
      [itemKey]: text
    }));
  };

  const toggleTrackingType = value => {
    setState(prev => ({
      ...prev,
      type: value ? 'kpi1' : 'budget'
    }));
  };

  const toggleFrequency = value => {
    setState(prev => ({
      ...prev,
      frequencyWeekly: value
    }));
  };

  const toggleSendEmails = () => {
    setState(prev => ({
      ...prev,
      sendEmails: !prev.sendEmails
    }));
  };

  const onAutoCompliteChange = (data, reason) => {
    if (reason === 'select-option' || reason === 'selectOption') {
      handleChange('funnelId')(data?.id);
    }

    if (reason === 'clear') {
      handleChange('funnelId')('');
    }
  };
  const onNext = () => {
    let error = false;

    if (!funnelId) {
      toggleError('funnelId', true);
      error = true;
    }

    if (!error) {
      const datas = {
        funnelId,
        sheetUrl,
        toleranceLimits,
        toleranceLimit: toleranceLimits[0] || 10,
        alerting: sendEmails,
        alertingFrequence: frequencyWeekly ? 'weekly' : 'daily',
        type,
        emails
      };

      onValidate(datas);
      resetStates();
    }
  };

  const getActions = () => {
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            <Button
              variant="contained"
              color={themeColors.funnel}
              size="medium"
              widthSize="medium"
              onClick={onNext}
            >
              {selected ? 'Modifier' : 'Ajouter'} la configuration
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  const searchOptionsKeys = ['name', 'id'];

  return (
    <Modal
      actions={getActions()}
      {...props}
      onClose={() => {
        resetStates();
        onClose();
      }}
    >
      <Container>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Suivi">
              <Container>
                <Row spacing={1} justify="space-between">
                  <Item xs={5} justify="flex-start">
                    <AutocompleteTextField
                      title="Funnel"
                      required
                      options={funnels}
                      formatLabel={option => {
                        let str = option[searchOptionsKeys[0]];
                        if (searchOptionsKeys[1]) {
                          str += ` (${option[searchOptionsKeys[1]]})`;
                        }
                        return str;
                      }}
                      small
                      value={funnels.find(el => el.id === funnelId)}
                      onChange={onAutoCompliteChange}
                      placeholder="Choisissez un funnel"
                      disabled={selected?.funnelId}
                      error={funnelIdError}
                      bgColor={themeColors.primary}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <TextField
                      title="URL Google Sheet"
                      small
                      value={sheetUrl}
                      onChange={handleChange('sheetUrl')}
                      placeholder="Saisissez un URL"
                      bgColor={themeColors.primary}
                    />
                  </Item>
                </Row>
                <Row spacing={1} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Switch
                      title="Type de suivi"
                      required
                      checked={type === 'kpi1'}
                      onChange={toggleTrackingType}
                      options={['Budgétaire', 'Impressions']}
                    />
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Switch
                      title="Périodicité"
                      required
                      checked={frequencyWeekly}
                      onChange={toggleFrequency}
                      options={['Quotidienne', 'Hebdomadaire']}
                    />
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Tolérance et alerting">
              <Container>
                <Row
                  spacing={1}
                  justify="flex-start"
                  style={{ marginBottom: '40px' }}
                >
                  <Item justify="flex-start">
                    <Text>Seuils de tolérance</Text>
                    <ListField
                      variant="customFields"
                      labels={['Seuil de tolérance (%)']}
                      values={toleranceLimits.map(e => ({ name: `${e}` }))}
                      onChange={value => {
                        const newValues = value.map(el =>
                          parseInt(el.name, 10)
                        );
                        handleChange('toleranceLimits')(newValues);
                      }}
                      actionTxt="Ajouter un seuil"
                      deleteAction
                      inputs={['TextField']}
                      options={['name']}
                      isRequireds={[true]}
                      placeholder="Choisissez un seuil"
                      bgColor={themeColors.primary}
                      fieldsFormat={['numberDec']}
                    />
                  </Item>
                </Row>
                <Row spacing={1} justify="flex-start">
                  <Item direction="column" alignItems="flex-start">
                    <Text>Activer les alertes mail</Text>
                    <Switch checked={sendEmails} onChange={toggleSendEmails} />
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        {sendEmails && (
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Adresses d'alerting">
                <Container>
                  <Row spacing={1} justify="flex-start">
                    <Item xs={12} justify="flex-start">
                      <ListField
                        variant="customFields"
                        labels={['Adresse email']}
                        values={emails.map(email => ({ email }))}
                        onChange={values =>
                          handleChange('emails')(values.map(v => v.email))
                        }
                        actionTxt="Ajouter un mail"
                        deleteAction
                        inputs={['TextField']}
                        options={['email']}
                        isRequireds={[true]}
                        placeholder="Saisissez une adresse email"
                        bgColor={themeColors.primary}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
        )}
      </Container>
    </Modal>
  );
};

ModalForm.defaultProps = {
  onClose: () => null
};
ModalForm.propTypes = {
  onValidate: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  config: PropTypes.objectOf(PropTypes.any).isRequired
};

export default ModalForm;
